import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetApplicationsCountQuery } from '@/entities/Applicants';
import {
  DELETIONS_ATTENTION_HREF,
  useGetAccountDeletionProblemsCountQuery,
} from '@/entities/Students';
import { useChatUnreadTotal } from '@/features/Chat';
import { MENU } from '@/shared/config/menu';
import { routerPath } from '@/shared/config/router';
import { filterMenuByPageAccess, useAdminAccess } from '@/shared/lib/adminAccess';
import { NestedMenuItem } from '../../components/NestedMenuItem';
import { SimpleMenuItem } from '../../components/SimpleMenuItem';
import styles from './Sidebar.module.scss';

export const Sidebar = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const { pageAccess, isLoading, canAccessPage } = useAdminAccess();
  const chatUnreadTotal = useChatUnreadTotal();
  const canSeeApplicants = canAccessPage('applicants');
  const { data: applicationsCountData } = useGetApplicationsCountQuery(undefined, {
    skip: !canSeeApplicants,
  });

  // Заявки на удаление, которым нужен человек: счётчик на Profiles, где их вкладка
  const { data: deletionProblemsData } = useGetAccountDeletionProblemsCountQuery(undefined, {
    skip: !canAccessPage('students'),
  });

  const applicantsSubmittedCount = applicationsCountData?.data?.submitted ?? 0;
  const deletionProblemsCount = deletionProblemsData?.data?.total ?? 0;

  const visibleMenu = useMemo(() => {
    // Пока права не пришли, разделы не показываем: иначе недоступные мелькают до фильтрации
    if (isLoading) return [];
    return filterMenuByPageAccess(MENU, pageAccess);
  }, [isLoading, pageAccess]);

  const studentsChildBadgeCounts = useMemo(
    () => ({
      [routerPath.applicants]: applicantsSubmittedCount,
      [routerPath.profiles]: deletionProblemsCount,
    }),
    [applicantsSubmittedCount, deletionProblemsCount],
  );

  // Счётчик Profiles открывает заявки, которым нужен человек, а не список учётных записей
  const studentsChildBadgeHrefs = { [routerPath.profiles]: DELETIONS_ATTENTION_HREF };

  return (
    <div className={styles.container}>
      {visibleMenu.length > 0 && (
        <ul className="flex flex-col gap-4" aria-label="Main menu">
          {visibleMenu.map((item) => {
            if (item.children && item.children.length > 0) {
              return (
                <NestedMenuItem
                  key={item.id}
                  item={item}
                  pathname={pathname}
                  childBadgeCounts={
                    item.children.some((child) => child.href === routerPath.applicants)
                      ? studentsChildBadgeCounts
                      : undefined
                  }
                  childBadgeHrefs={studentsChildBadgeHrefs}
                />
              );
            }

            return (
              <li key={item.id}>
                <SimpleMenuItem
                  item={item}
                  pathname={pathname}
                  badgeCount={item.href === routerPath.chats ? chatUnreadTotal : undefined}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
